import { STOCK_BUCKET_LABELS, type StockBucket } from "@artisancode/api-types";
import { Package, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DatePicker } from "@/components/shared/date-picker";
import { SearchableSelect } from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { StockAdjustmentCountCard } from "./stock-adjustment-count-card";
import { useStockAdjustmentForm } from "./use-stock-adjustment-form";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function StockAdjustmentDialog({
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const { preview: sequencePreview } = useNextNumberPreview("stock_adjustment");
  const form = useStockAdjustmentForm({ open, onOpenChange, onSuccess });
  const { itemType, selectedItem, difference, isSubmitting } = form;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setConfirmOpen(true);
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              <span>Buat Penyesuaian Stok</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Koreksi langsung persediaan untuk kasus darurat (tumpah, rusak,
              expired, temuan) tanpa harus menjalankan full opname.
              {sequencePreview && (
                <span className="block mt-1 font-mono text-muted-foreground">
                  Nomor dokumen: {sequencePreview}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            {/* Tanggal & Tipe Item */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="adj-date" className="text-xs">
                  Tanggal Transaksi <span className="text-destructive">*</span>
                </Label>
                <DatePicker value={form.date} onChange={form.setDate} />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Tipe Barang <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={itemType}
                  onValueChange={(val: "raw_material" | "product") =>
                    form.setItemType(val)
                  }
                >
                  <SelectTrigger className="text-xs h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="raw_material" className="text-xs">
                      Bahan Baku
                    </SelectItem>
                    <SelectItem value="product" className="text-xs">
                      Produk Jadi
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pilih Barang */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                Pilih {itemType === "raw_material" ? "Bahan Baku" : "Produk"}{" "}
                <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                value={form.selectedItemId}
                onChange={form.selectItem}
                disabled={form.isLoadingStocks}
                placeholder={
                  form.isLoadingStocks
                    ? "Memuat data barang..."
                    : "Cari barang..."
                }
                options={form.filteredItems.map((item) => ({
                  value: item.itemId,
                  label: item.name,
                  hint: `(${item.uomName}) — Total: ${item.totalStock}`,
                }))}
                className="text-xs"
              />
            </div>

            {/* Wadah / Lokasi */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                Lokasi / Wadah Penyimpanan{" "}
                <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.bucket}
                onValueChange={(b: StockBucket) => form.selectBucket(b)}
              >
                <SelectTrigger className="text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on_hand" className="text-xs">
                    {STOCK_BUCKET_LABELS.on_hand}
                  </SelectItem>
                  {itemType === "raw_material" && (
                    <SelectItem value="repacking" className="text-xs">
                      {STOCK_BUCKET_LABELS.repacking}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedItem && (
              <StockAdjustmentCountCard
                item={selectedItem}
                systemQty={form.systemQty}
                countedQtyStr={form.countedQtyStr}
                difference={difference}
                onCountedChange={form.setCountedQtyStr}
              />
            )}

            {/* Alasan Penyesuaian */}
            <div className="space-y-1.5">
              <Label className="text-xs">
                Alasan Penyesuaian <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                value={form.reasonId}
                onChange={form.setReasonId}
                placeholder="Cari alasan selisih..."
                options={form.applicableReasons.map((reason) => ({
                  value: reason.id,
                  label: reason.name,
                  hint: `[${reason.direction}]`,
                }))}
                className="text-xs"
              />
            </div>

            {/* Catatan Tambahan */}
            <div className="space-y-1.5">
              <Label htmlFor="adj-note" className="text-xs">
                Catatan / Keterangan (Opsional)
              </Label>
              <Textarea
                id="adj-note"
                value={form.note}
                onChange={(e) => form.setNote(e.target.value)}
                placeholder="Contoh: Botol sirup pecah saat restock di bar..."
                className="text-xs min-h-[60px]"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="min-h-[40px] w-full sm:w-auto text-xs"
            >
              Batal
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                isSubmitting ||
                !form.selectedItemId ||
                difference === 0 ||
                !form.reasonId
              }
              className="min-h-[40px] w-full sm:w-auto text-xs gap-1.5"
            >
              <Package className="h-3.5 w-3.5" />
              {isSubmitting ? "Menyimpan..." : "Simpan Penyesuaian"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Simpan penyesuaian stok?"
        description={`Stok ${selectedItem?.name ?? "barang"} langsung berubah ${
          difference > 0 ? "+" : ""
        }${difference} ${selectedItem?.uomName ?? ""} dan tercatat di Kartu Stok.`}
        confirmLabel="Ya, Simpan"
        variant="default"
        isConfirming={isSubmitting}
        onConfirm={async () => {
          await form.submit();
          setConfirmOpen(false);
        }}
      />
    </Dialog>
  );
}
